import type { ComputedRef } from 'vue'
import { ref } from 'vue'
import * as THREE from 'three'
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js'
import type { SceneManager } from '@/three/SceneManager'
import { useSceneStore, useSelectionStore } from '@/stores/scene'

type SceneStore = ReturnType<typeof useSceneStore>
type SelectionStore = ReturnType<typeof useSelectionStore>

export interface MeasureToolDeps {
  getSm: () => SceneManager | undefined
  sceneStore: SceneStore
  selectionStore: SelectionStore
  interactionReadonly: ComputedRef<boolean>
}

export function createMeasureTool(deps: MeasureToolDeps) {
  let measureRoot: THREE.Group | null = null
  let measureFirstPt: THREE.Vector3 | null = null
  let measureFirstDot: THREE.Mesh | null = null
  let measureDraftLine: THREE.Line | null = null
  let measureDraftLabelObj: CSS2DObject | null = null

  const measureHasDraft = ref(false)

  function horizDistXZ(a: THREE.Vector3, b: THREE.Vector3) {
    const dx = a.x - b.x
    const dz = a.z - b.z
    return Math.sqrt(dx * dx + dz * dz)
  }

  function addMeasureSegmentLine(p0: THREE.Vector3, p1: THREE.Vector3): THREE.Line {
    const geo = new THREE.BufferGeometry().setFromPoints([p0.clone(), p1.clone()])
    return new THREE.Line(geo, new THREE.LineBasicMaterial({ color: 0x55ee99 }))
  }

  function disposeMeasureGroup(g: THREE.Group) {
    g.traverse((o) => {
      if (o instanceof THREE.Line) {
        o.geometry.dispose()
        ;(o.material as THREE.Material).dispose()
      }
      if ((o as unknown as { isCSS2DObject?: boolean }).isCSS2DObject) {
        ;(o as CSS2DObject).element.parentElement?.removeChild((o as CSS2DObject).element)
      }
    })
    measureRoot?.remove(g)
  }

  function makeMeasureLabelEl(text: string, onRemove: () => void): CSS2DObject {
    const wrap = document.createElement('div')
    wrap.style.cssText =
      'display:flex;align-items:center;gap:6px;pointer-events:auto;background:rgba(10,22,18,.88);color:#b8ffd0;padding:4px 8px;border-radius:6px;font-size:12px;border:1px solid #3a8a60;'
    const span = document.createElement('span')
    span.textContent = text
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.textContent = '×'
    btn.setAttribute('aria-label', '删除标注')
    btn.style.cssText =
      'cursor:pointer;border:none;background:#2a5040;color:#9edabb;width:22px;height:22px;border-radius:4px;line-height:1;font-size:14px;'
    btn.addEventListener('click', (ev) => {
      ev.stopPropagation()
      onRemove()
    })
    wrap.appendChild(span)
    wrap.appendChild(btn)
    return new CSS2DObject(wrap)
  }

  function getMeasureFloorPoint(e: MouseEvent): THREE.Vector3 | null {
    const sm = deps.getSm()
    if (!sm) return null
    const canvas = sm.renderer.domElement
    const floor = deps.sceneStore.activeFloors().find((f) => f.id === deps.selectionStore.currentFloorId)
    const yElev = floor?.elevation ?? 0
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -yElev)
    const raycaster = new THREE.Raycaster()
    const rect = canvas.getBoundingClientRect()
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1
    )
    raycaster.setFromCamera(ndc, sm.camera)
    const pt = new THREE.Vector3()
    return raycaster.ray.intersectPlane(plane, pt) ? pt : null
  }

  function clearMeasureDraft() {
    measureHasDraft.value = false
    measureFirstPt = null
    if (measureFirstDot) {
      ;(measureFirstDot.geometry as THREE.BufferGeometry).dispose()
      ;(measureFirstDot.material as THREE.Material).dispose()
      measureRoot?.remove(measureFirstDot)
      measureFirstDot = null
    }
    if (measureDraftLine) {
      measureDraftLine.geometry.dispose()
      ;(measureDraftLine.material as THREE.Material).dispose()
      measureRoot?.remove(measureDraftLine)
      measureDraftLine = null
    }
    if (measureDraftLabelObj) {
      measureDraftLabelObj.element.parentElement?.removeChild(measureDraftLabelObj.element)
      measureRoot?.remove(measureDraftLabelObj)
      measureDraftLabelObj = null
    }
  }

  function onMeasureCanvasClick(e: MouseEvent) {
    const sm = deps.getSm()
    if (!sm || deps.interactionReadonly.value || !deps.selectionStore.measureToolActive) return
    if (e.button !== 0) return
    const canvas = sm.renderer.domElement
    if (!canvas.contains(e.target as Node)) return

    const pt = getMeasureFloorPoint(e)
    if (!pt || !measureRoot) return

    e.preventDefault()
    e.stopImmediatePropagation()

    const floor = deps.sceneStore.activeFloors().find((f) => f.id === deps.selectionStore.currentFloorId)
    const yElev = floor?.elevation ?? 0

    if (!measureFirstPt) {
      measureFirstPt = pt.clone()
      measureHasDraft.value = true
      const dotGeo = new THREE.SphereGeometry(0.1, 8, 8)
      const dotMat = new THREE.MeshBasicMaterial({ color: 0x55ee99 })
      measureFirstDot = new THREE.Mesh(dotGeo, dotMat)
      measureFirstDot.position.set(pt.x, yElev + 0.05, pt.z)
      measureRoot.add(measureFirstDot)
      sm.markDirty()
    } else {
      const p0 = measureFirstPt
      const p1 = pt.clone()
      clearMeasureDraft()

      const d = horizDistXZ(p0, p1)
      const g = new THREE.Group()
      g.add(addMeasureSegmentLine(p0, p1))
      const mx = (p0.x + p1.x) / 2
      const mz = (p0.z + p1.z) / 2
      const label = makeMeasureLabelEl(`${d.toFixed(2)} m`, () => {
        disposeMeasureGroup(g)
        deps.getSm()?.markDirty()
      })
      label.position.set(mx, yElev + 0.1, mz)
      g.add(label)
      measureRoot.add(g)
      sm.markDirty()
    }
  }

  function onMeasureCanvasMove(e: MouseEvent) {
    const sm = deps.getSm()
    if (!sm || !measureFirstPt || !measureRoot) return
    const pt = getMeasureFloorPoint(e)
    if (!pt) return

    const floor = deps.sceneStore.activeFloors().find((f) => f.id === deps.selectionStore.currentFloorId)
    const yElev = floor?.elevation ?? 0
    const d = horizDistXZ(measureFirstPt, pt)
    const mx = (measureFirstPt.x + pt.x) / 2
    const mz = (measureFirstPt.z + pt.z) / 2

    if (measureDraftLine) {
      measureDraftLine.geometry.dispose()
      measureDraftLine.geometry = new THREE.BufferGeometry().setFromPoints([measureFirstPt.clone(), pt.clone()])
    } else {
      const geo = new THREE.BufferGeometry().setFromPoints([measureFirstPt.clone(), pt.clone()])
      measureDraftLine = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: 0x55ee99, transparent: true, opacity: 0.5 }))
      measureRoot.add(measureDraftLine)
    }

    if (measureDraftLabelObj) {
      const span = measureDraftLabelObj.element.querySelector('span') as HTMLElement
      if (span) span.textContent = `${d.toFixed(2)} m`
      measureDraftLabelObj.position.set(mx, yElev + 0.1, mz)
    } else {
      const wrap = document.createElement('div')
      wrap.style.cssText =
        'pointer-events:none;background:rgba(10,22,18,.7);color:#b8ffd0;padding:3px 8px;border-radius:5px;font-size:12px;border:1px solid #3a8a60;'
      const span = document.createElement('span')
      span.textContent = `${d.toFixed(2)} m`
      wrap.appendChild(span)
      measureDraftLabelObj = new CSS2DObject(wrap)
      measureDraftLabelObj.position.set(mx, yElev + 0.1, mz)
      measureRoot.add(measureDraftLabelObj)
    }

    sm.markDirty()
  }

  function teardownMeasureToolVisuals() {
    clearMeasureDraft()
    if (!measureRoot) return
    const children = [...measureRoot.children]
    for (const c of children) {
      if (c instanceof THREE.Group) disposeMeasureGroup(c)
      else {
        if ((c as unknown as { isCSS2DObject?: boolean }).isCSS2DObject) {
          ;(c as CSS2DObject).element.parentElement?.removeChild((c as CSS2DObject).element)
        }
        measureRoot.remove(c)
      }
    }
    deps.getSm()?.markDirty()
  }

  function initMeasureRoot() {
    const sm = deps.getSm()
    if (!sm || measureRoot) return
    measureRoot = new THREE.Group()
    measureRoot.name = '__measure_tool__'
    sm.scene.add(measureRoot)
  }

  function detachMeasureRootFromScene() {
    const sm = deps.getSm()
    if (measureRoot && sm) {
      sm.scene.remove(measureRoot)
    }
    measureRoot = null
  }

  function hasMeasureDraftPoint() {
    return measureFirstPt !== null
  }

  return {
    measureHasDraft,
    onMeasureCanvasClick,
    onMeasureCanvasMove,
    teardownMeasureToolVisuals,
    clearMeasureDraft,
    hasMeasureDraftPoint,
    initMeasureRoot,
    detachMeasureRootFromScene,
  }
}
